import { useMemo } from 'react';
import { Chip, Theme, styled, useTheme } from '@mui/material';
import { TaskProgress } from 'models/tasks';
import { OverridableStringUnion } from '@mui/types';

const StatusChip = styled(Chip)(({ theme }) => ({
    color: '#333333 !important',
    flexDirection: 'row-reverse',    
    fontSize: '12px',
    fontFamily: 'Open Sans',
    fontWeight: '400',
    lineHeight: '20px',
    borderRadius: '26px',
    '&:focus, &:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`, 
    },
    '&.MuiChip-colorWarning': {
        color: theme.palette.warning.dark,
        border: `1px solid ${theme.palette.warning.dark}`,
        '&:focus, &:focus-visible': {
            outline: `2px solid ${theme.palette.warning.dark}`,
            outlineOffset: '2px',
            boxShadow: `0 0 0 3px ${theme.palette.warning.dark}33`,
        },
    },
    '&.MuiChip-colorSuccess': {
        color: theme.palette.success.dark,
        border: `1px solid ${theme.palette.success.dark}`,
        '&:focus, &:focus-visible': {
            outline: `2px solid ${theme.palette.success.dark}`,
            outlineOffset: '2px',
            boxShadow: `0 0 0 3px ${theme.palette.success.dark}33`,
        },
    },
    '&.MuiChip-colorPrimary': {
        color: theme.palette.primary.dark,
        border: `1px solid ${theme.palette.primary.dark}`,
        '&:focus, &:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
            boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`,
        },
    },
    '&.MuiChip-colorError': {
        color: theme.palette.error.main,
        border: `1px solid ${theme.palette.error.dark}`,
        '&:focus, &:focus-visible': {
            outline: `2px solid ${theme.palette.error.main}`,
            outlineOffset: '2px',
            boxShadow: `0 0 0 3px ${theme.palette.error.main}33`,
        },
    },
    '& .MuiChip-icon': {
        width: '12px',
        height: '12px',
        borderRadius: '.5em'
    },
    '& .MuiChip-iconSmall': {
        marginLeft: '0',
        marginRight: '4px'
    },
    '& .MuiChip-label': {
        fontFamily: 'Open Sans',
        fontWeight: 400,
    },
    '& .MuiChip-iconColorPrimary': {
        backgroundColor: theme.palette.primary.main
    },
    '& .MuiChip-iconColorSuccess': {
        backgroundColor: theme.palette.success.main
    },
    '& .MuiChip-iconColorWarning': {
        backgroundColor: theme.palette.warning.dark
    },
    '& .MuiChip-iconColorError': {
        backgroundColor: theme.palette.error.main    
    },
    '&.MuiChip-filled .MuiChip-icon': {        
        border: `1px solid #fff`
    }
}));

interface TaskProgressPillProps {
    progress?: TaskProgress | null;
    theme?: Theme;
    tabIndex?: number;
    "aria-label"?: string;
} 

interface TaskProgressChipSettings {
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    variant: OverridableStringUnion<"outlined" | "filled">,
    text: string
}

const TaskProgressPill = (props: TaskProgressPillProps) => {
    const localTheme = useTheme();
    const theme = props.theme ?? localTheme;
    const chipSettings = useMemo((): TaskProgressChipSettings => {
        switch (props.progress?.toLowerCase().replace(' ', '-')) {
            case 'on-schedule':
            default:
                return {
                    text: 'On Schedule',
                    color: 'success',
                    variant: 'outlined'
                }
            case 'at-risk':
                return {
                    text: 'At Risk',
                    color: 'error',
                    variant: 'outlined'
                }
            case 'running-late':
                return {
                    text: 'Running Late',
                    color: 'warning',
                    variant: 'outlined'
                }
        }
    }, [props.progress]);
    
    const ariaLabel = props["aria-label"] || `Task progress: ${chipSettings.text}`;

    return (props.progress === undefined || props.progress === null || props.progress === '')
        ? <></>
        : <StatusChip
            tabIndex={props.tabIndex ?? 0}
            size='small'
            icon={<i/>}
            variant={chipSettings.variant}
            color={chipSettings.color}
            label={chipSettings.text}
            aria-label={ariaLabel}
            role="status"
            clickable
            onClick={(e) => e.currentTarget.focus()}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                }
            }}
        />;
}

export default TaskProgressPill;